import React, { useState, cloneElement, useCallback } from 'react';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { connect } from 'react-redux';
import flowRight from 'lodash.flowright';

import withUserCheck from '../../../utils/withUserCheck';
import { translate } from '../../../i18n/I18nProvider';
import can from '../../../utils/can';
import createSubject from '../../../utils/createSubject';
import ChangeSubscription from './ChangeSubscription';
import Optional from '../../../utils/optional';
import CancelSubscriptionConfimation from './CancelSubscriptionConfimation';
import cancelSubscription from '../actions/cancelSubscription';
import ConfirmDialog from '../../../partials/confirmationDialog/ConfirmDialog';
import { actions as reduxActions } from '../reducers';
import { actions as editSimcardReduxActions } from '../../../modals/editSimcard/reducers';
import getSimcard from '../actions/getSimcard';
import { isDefined, isUndefined } from '../../../utils/isDefined';
import { actions as errorReduxActions } from '../../../partials/errorDialog/reducers';
import EditUserInfo from './EditUserInfo';

const MoreActionsMenu = ({
  children,
  subscription = {},
  clientId,
  openSummary,
  openEditSimcard,
  openErrorDialog,
  setRefresh,
  category = 'IoT'
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onCancelConfirmation = async () => {
    const { requestCompleted, summaryInfo } = await cancelSubscription({
      clientId,
      externalReference: subscription?.externalReference,
      name: subscription?.name
    });

    if (requestCompleted) {
      openSummary({ summaryInfo });
      handleClose();
    }
  };

  const onEditClick = useCallback(
    async () => {
      handleClose();

      if (!isDefined(subscription?.icc)) {
        openErrorDialog({
          directMessage: translate('SWAP_SIMCARD.NO_ICC'),
          dialogTitle: translate('NUMBER.SIMCARD')
        });
      }

      const { simcard, errors } = await getSimcard({
        clientId,
        icc: subscription.icc
      });

      if (isUndefined(simcard) || isDefined(errors)) {
        openErrorDialog({
          directMessage: translate('SWAP_SIMCARD.NO_DATA'),
          dialogTitle: translate('NUMBER.SIMCARD')
        });
      } else {
        openEditSimcard({
          simcard,
          simcardId: simcard.id,
          clientId: simcard.client_id
        });
      }
    },
    // eslint-disable-next-line
    [clientId, subscription]
  );

  return (
    <div>
      {cloneElement(children, { onClick: handleClick })}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <div>
          <ChangeSubscription
            subscription={subscription}
            closeMenu={handleClose}
            setRefresh={setRefresh}
            fromIoT={category === 'IoT'}
            skuPrefix={category === 'MBB' ? 'MBB' : undefined}
          >
            <MenuItem
              disabled={
                !can(
                  'change',
                  createSubject('Quote::Subscription', {
                    client_id: Number(clientId)
                  })
                ) || subscription?.status !== 'ACTIVE'
              }
            >
              {translate('SUBSCRIPTIONS.CHANGE_SUBSCRIPTION')}
            </MenuItem>
          </ChangeSubscription>
        </div>
        <div>
          <ConfirmDialog
            onConfirmClick={onCancelConfirmation}
            dialogContentComponent={<CancelSubscriptionConfimation />}
            dialogTitleIntlId={'SUBSCRIPTIONS.CANCEL_SUBSCRIPTION'}
            closeMenu={handleClose}
          >
            <MenuItem
              disabled={
                !can(
                  'update',
                  createSubject('Quote::Subscription', {
                    client_id: Number(clientId)
                  })
                ) || subscription?.status !== 'ACTIVE'
              }
            >
              {translate('SUBSCRIPTIONS.CANCEL_SUBSCRIPTION')}
            </MenuItem>
          </ConfirmDialog>
        </div>
        <MenuItem
          onClick={onEditClick}
          disabled={
            !can(
              'update',
              createSubject('SimCard', {
                client_id: Number(clientId)
              })
            )
          }
        >
          {translate('TOOLTIP.EDIT_SIMCARD')}
        </MenuItem>
        <EditUserInfo
          subscription={subscription}
          clientId={clientId}
          closeMenu={handleClose}
          setRefresh={setRefresh}
        >
          <MenuItem
            disabled={
              !can(
                'update',
                createSubject('Quote::Subscription', {
                  client_id: Number(clientId)
                })
              ) || isDefined(subscription?.accountUserReference)
            }
          >
            {translate('TOOLTIP.EDIT_USER_INFO')}
          </MenuItem>
        </EditUserInfo>
      </Menu>
    </div>
  );
};

const mapStatesToProps = ({ selectClient }) => {
  return {
    clientId: Optional(selectClient.selectedClient?.id).or(void 0)
  };
};

const mapDispatchToProps = {
  openSummary: reduxActions.openSummary,
  openEditSimcard: editSimcardReduxActions.openEditSimcard,
  openErrorDialog: errorReduxActions.openDialog
};

export default flowRight(
  withUserCheck,
  connect(mapStatesToProps, mapDispatchToProps)
)(MoreActionsMenu);
